// /opt/isp_erp/frontend/src/pages/tickets/CreateInstalacionTicket.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
  Pagination,
  Stack,
  InputAdornment,
  IconButton
} from '@mui/material';
import {
  Send, Cancel, LocationOn, Person, Phone,
  Search, Clear, Engineering
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ticketsAPI } from '../../api/tickets.api';
import axios from '../../api/axios';
import { scheduleAPI } from '../../api/schedule.api';

const CreateInstalacionTicket = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [cargandoDatos, setCargandoDatos] = useState(true);
  const [instalacionesAprobadas, setInstalacionesAprobadas] = useState([]);
  const [selectedInstalacion, setSelectedInstalacion] = useState(null);
  const [searchInstalacion, setSearchInstalacion] = useState('');
  const [page, setPage] = useState(1);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Estados para verificación de disponibilidad
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [isAvailable, setIsAvailable] = useState(null);
  const [availabilityError, setAvailabilityError] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);

  const itemsPerPage = 10;
  const [formData, setFormData] = useState({
    installation_id: '',
    title: '',
    description: '',
    priority: 'media',
    contact_phone: '',
    contact_phone_alt: '',
    scheduled_date: '',
    vineta: '',
    ficha: ''
  });

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    try {
      const instalacionesRes = await axios.get('/installations/');
      const todasInstalaciones = instalacionesRes.data || [];
      const aprobadas = todasInstalaciones.filter(inst => inst.estado === 'APROBADA');
      setInstalacionesAprobadas(aprobadas);
    } catch (error) {
      console.error('Error cargando datos:', error);
      setError('Error al cargar datos necesarios');
    } finally {
      setCargandoDatos(false);
    }
  };

  // Verificar disponibilidad del técnico
  const checkAvailability = async (dateTime) => {
    if (!selectedInstalacion?.tecnico_id || !dateTime) {
      setIsAvailable(null);
      return;
    }

    setCheckingAvailability(true);
    setAvailabilityError('');
    setIsAvailable(null);
    setAvailableSlots([]);

    try {
      const result = await scheduleAPI.checkAvailability(
        selectedInstalacion.tecnico_id,
        dateTime,
        90
      );

      setIsAvailable(result.available);

      if (!result.available) {
        setAvailabilityError('El técnico no está disponible en este horario');
        loadAvailableSlots(dateTime);
      }
    } catch (err) {
      console.error('Error checking availability:', err);
      setAvailabilityError('Error al verificar disponibilidad');
    } finally {
      setCheckingAvailability(false);
    }
  };

  const loadAvailableSlots = async (dateTime) => {
    if (!selectedInstalacion?.tecnico_id || !dateTime) return;

    try {
      const result = await scheduleAPI.getAvailableSlots(
        selectedInstalacion.tecnico_id,
        dateTime,
        90
      );
      setAvailableSlots(result.available_slots || []);
    } catch (err) {
      console.error('Error loading available slots:', err);
    }
  };

  const handleDateTimeChange = (e) => {
    const dateTime = e.target.value;
    setFormData(prev => ({ ...prev, scheduled_date: dateTime }));
    if (dateTime && selectedInstalacion?.tecnico_id) {
      checkAvailability(dateTime);
    } else {
      setIsAvailable(null);
      setAvailabilityError('');
      setAvailableSlots([]);
    }
  };

  const selectAlternativeSlot = (slot) => {
    const dateTime = new Date(slot.start_time);
    const formattedDateTime = dateTime.toISOString().slice(0, 16);
    setFormData(prev => ({ ...prev, scheduled_date: formattedDateTime }));
    checkAvailability(formattedDateTime);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const seleccionarInstalacion = (instalacion) => {
    setSelectedInstalacion(instalacion);

    setIsAvailable(null);
    setAvailabilityError('');
    setAvailableSlots([]);

    if (!instalacion.tecnico_id) {
      setError('La instalación no tiene técnico asignado.');
    }

    setFormData(prev => ({
      ...prev,
      installation_id: instalacion.id,
      contact_phone: instalacion.telefono || '',
      contact_phone_alt: instalacion.contacto_alternativo || '',
      title: `Instalación - ${instalacion.nombres} ${instalacion.apellidos}`,
      vineta: instalacion.vineta || '',
      ficha: instalacion.ficha || '',
      description: `Instalación de equipo en ${instalacion.direccion || 'dirección no especificada'}\nServicio: ${instalacion.servicio || 'N/A'}\nPlan: ${instalacion.plan || 'N/A'}\nTécnico asignado: ${instalacion.tecnico_nombre || instalacion.tecnico_id || 'No asignado'}\nViñeta: ${instalacion.vineta || 'N/A'}\nFicha: ${instalacion.ficha || 'N/A'}`
    }));
  };

  const instalacionesFiltradas = instalacionesAprobadas.filter(inst => {
    const nombreCompleto = `${inst.nombres || ''} ${inst.apellidos || ''}`.toLowerCase();
    const busqueda = searchInstalacion.toLowerCase();
    return nombreCompleto.includes(busqueda) ||
           inst.identificacion?.includes(busqueda) ||
           inst.direccion?.toLowerCase().includes(busqueda) ||
           inst.telefono?.includes(busqueda);
  });

  const totalPages = Math.ceil(instalacionesFiltradas.length / itemsPerPage);
  const paginatedInstalaciones = instalacionesFiltradas.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const limpiarBusquedaInstalacion = () => {
    setSearchInstalacion('');
    setPage(1);
  };

  // ✅ HandleSubmit SIN equipos
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.installation_id) {
      setError('Debe seleccionar una instalación aprobada');
      return;
    }
    if (!formData.contact_phone) {
      setError('Debe ingresar un teléfono de contacto');
      return;
    }
    if (formData.scheduled_date && isAvailable !== true) {
      setError('Debe seleccionar un horario disponible para el técnico');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await ticketsAPI.createInstalacionTicket({
        installation_id: formData.installation_id,
        contact_phone: formData.contact_phone,
        contact_phone_alt: formData.contact_phone_alt,
        title: formData.title,
        description: formData.description,
        priority: formData.priority,
        scheduled_date: formData.scheduled_date || null,
        vineta: formData.vineta,
        ficha: formData.ficha
      });

      setSuccess(true);
      setTimeout(() => {
        navigate('/tickets');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al crear el ticket de instalación');
    } finally {
      setLoading(false);
    }
  };

  if (cargandoDatos) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Cargando instalaciones aprobadas...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Ticket de Instalación
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Crear una orden de trabajo para instalar nuevos equipos. Seleccione una instalación aprobada.
        <br />
        <strong>Nota:</strong> Los equipos se seleccionarán al momento de cerrar el ticket.
      </Typography>

      <Grid container spacing={3}>
        {/* Columna 1: Instalaciones Aprobadas */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Instalaciones Aprobadas
                <Chip label={`${instalacionesAprobadas.length} total`} size="small" sx={{ ml: 1 }} />
              </Typography>

              <TextField
                fullWidth
                placeholder="Buscar por nombre, cédula, teléfono o dirección..."
                value={searchInstalacion}
                onChange={(e) => { setSearchInstalacion(e.target.value); setPage(1); }}
                size="small"
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (<InputAdornment position="start"><Search /></InputAdornment>),
                  endAdornment: searchInstalacion && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={limpiarBusquedaInstalacion}><Clear /></IconButton>
                    </InputAdornment>
                  )
                }}
              />

              {selectedInstalacion && (
                <Paper sx={{ p: 2, mb: 2, bgcolor: '#e3f2fd' }}>
                  <Typography variant="subtitle2" color="primary">Instalación seleccionada:</Typography>
                  <Box sx={{ mt: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Person fontSize="small" color="action" />
                      <Typography variant="body2" fontWeight="bold">
                        {selectedInstalacion.nombres} {selectedInstalacion.apellidos}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Phone fontSize="small" color="action" />
                      <Typography variant="body2">{selectedInstalacion.telefono || 'No especificado'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <LocationOn fontSize="small" color="action" />
                      <Typography variant="body2">{selectedInstalacion.direccion || 'No especificada'}</Typography>
                    </Box>
                    {selectedInstalacion.tecnico_id && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Engineering fontSize="small" color="action" />
                        <Typography variant="body2">Técnico: {selectedInstalacion.tecnico_nombre || selectedInstalacion.tecnico_id}</Typography>
                      </Box>
                    )}
                    <Chip label={`${selectedInstalacion.servicio || 'N/A'} - ${selectedInstalacion.plan || 'N/A'}`} size="small" color="info" />
                  </Box>
                </Paper>
              )}

              <List dense sx={{ maxHeight: 450, overflow: 'auto' }}>
                {paginatedInstalaciones.length === 0 ? (
                  <ListItem><ListItemText primary="No hay instalaciones aprobadas" /></ListItem>
                ) : (
                  paginatedInstalaciones.map(inst => (
                    <ListItem key={inst.id} button onClick={() => seleccionarInstalacion(inst)} selected={selectedInstalacion?.id === inst.id} sx={{ borderRadius: 1, mb: 0.5 }}>
                      <ListItemText
                        primary={<Typography variant="body2" fontWeight="bold">{inst.nombres || ''} {inst.apellidos || ''}</Typography>}
                        secondary={
                          <React.Fragment>
                            <Typography component="span" variant="caption" display="block">📞 {inst.telefono || 'Sin teléfono'} | 📋 {inst.identificacion || 'Sin ID'}</Typography>
                            <Typography component="span" variant="caption" display="block">📍 {(inst.direccion || 'Sin dirección').substring(0, 60)}...</Typography>
                            {inst.tecnico_id && <Typography component="span" variant="caption" display="block">🔧 Técnico ID: {inst.tecnico_id}</Typography>}
                          </React.Fragment>
                        }
                      />
                    </ListItem>
                  ))
                )}
              </List>

              {totalPages > 1 && (
                <Stack spacing={2} alignItems="center" sx={{ mt: 2 }}>
                  <Pagination count={totalPages} page={page} onChange={handlePageChange} color="primary" size="small" />
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Columna 2: Detalles del Ticket */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Detalles del Ticket</Typography>

              <TextField fullWidth label="Teléfono de Contacto" name="contact_phone" value={formData.contact_phone} onChange={handleChange} required size="small" sx={{ mb: 2 }} />
              <TextField fullWidth label="Teléfono Alternativo" name="contact_phone_alt" value={formData.contact_phone_alt} onChange={handleChange} size="small" sx={{ mb: 2 }} />

              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Prioridad</InputLabel>
                <Select name="priority" value={formData.priority} onChange={handleChange} label="Prioridad">
                  <MenuItem value="baja">Baja</MenuItem>
                  <MenuItem value="media">Media</MenuItem>
                  <MenuItem value="alta">Alta</MenuItem>
                  <MenuItem value="critica">Crítica</MenuItem>
                </Select>
              </FormControl>

              <TextField fullWidth type="datetime-local" label="Fecha Programada" name="scheduled_date" value={formData.scheduled_date} onChange={handleDateTimeChange} size="small" InputLabelProps={{ shrink: true }} sx={{ mb: 2 }} disabled={!selectedInstalacion?.tecnico_id} helperText={!selectedInstalacion?.tecnico_id ? "Primero seleccione una instalación con técnico" : ""} error={!!availabilityError} />

              {checkingAvailability && (<Box sx={{ display: 'flex', alignItems: 'center', mt: 0, mb: 1 }}><CircularProgress size={16} sx={{ mr: 1 }} /><Typography variant="caption">Verificando disponibilidad...</Typography></Box>)}
              {isAvailable === true && !checkingAvailability && formData.scheduled_date && (<Alert severity="success" sx={{ mt: 0, mb: 1, py: 0 }}>✅ Técnico disponible</Alert>)}
              {availabilityError && (<Alert severity="error" sx={{ mt: 0, mb: 1, py: 0 }}>{availabilityError}</Alert>)}

              {availableSlots.length > 0 && (
                <Box sx={{ mt: 1, mb: 2 }}>
                  <Typography variant="caption" gutterBottom display="block">Horarios sugeridos:</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {availableSlots.slice(0, 3).map((slot, idx) => (
                      <Chip key={idx} label={new Date(slot.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} onClick={() => selectAlternativeSlot(slot)} color="primary" variant="outlined" size="small" clickable />
                    ))}
                  </Box>
                </Box>
              )}

              <TextField fullWidth label="Título" name="title" value={formData.title} onChange={handleChange} required size="small" multiline rows={2} sx={{ mb: 2 }} />
              <TextField fullWidth label="Descripción" name="description" value={formData.description} onChange={handleChange} multiline rows={3} size="small" />

              <Divider sx={{ my: 2 }} />
              <Paper sx={{ p: 2, bgcolor: '#e8f5e9' }}>
                <Typography variant="body2" color="success.main">
                  <strong>✅ Importante:</strong> Los equipos se seleccionarán al cerrar el ticket. 
                  Al cerrar, los equipos cambiarán a <strong>INSTALADO</strong> y se descontarán los materiales del stock del técnico.
                </Typography>
              </Paper>

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                <Button variant="outlined" onClick={() => navigate('/tickets')} startIcon={<Cancel />} size="small">Cancelar</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={loading || !formData.installation_id || (formData.scheduled_date && isAvailable !== true)} startIcon={loading ? <CircularProgress size={20} /> : <Send />} size="small">
                  {loading ? 'Creando...' : 'Crear Ticket'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar open={success} autoHideDuration={3000} onClose={() => setSuccess(false)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="success">Ticket de instalación creado exitosamente</Alert>
      </Snackbar>

      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error">{error}</Alert>
      </Snackbar>
    </Box>
  );
};

export default CreateInstalacionTicket;
